// Canonical deal-stage order (mirrors the Task Template editor's STATUSES).
export const STAGE_ORDER = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
  "Under Contract",
  "Pending",
  "Closed",
  "Cancelled",
  "Expired",
];

// An item is a section header only when type === "section".
// Anything else (including legacy items with no `type`) is a task.
export const isSectionItem = (item) => item?.type === "section";

const cleanTaskItem = (t) => ({
  type: "task",
  id: t.id,
  title: t.title || "",
  status: "Open", // template tasks are always stored Open
  deadline: t.deadline ? String(t.deadline) : "",
  attachments: Array.isArray(t.attachments) ? t.attachments : [],
  assignee:
    t.assignee && t.assignee.id
      ? {
          id: t.assignee.id,
          firstname: t.assignee.firstname || "",
          lastname: t.assignee.lastname || "",
          profilepicture: t.assignee.profilepicture || "",
          role: t.assignee.role || "",
        }
      : null,
  createdAt: t.createdAt ? new Date(t.createdAt).toISOString() : null,
  coordinatorRole: !!t.coordinatorRole,
  transactionCoordinatorRole: !!t.transactionCoordinatorRole,
});

// Clean one stage's items for saving: keep section items, normalize tasks.
export const cleanTemplateItems = (items = []) =>
  items.map((it) =>
    isSectionItem(it)
      ? { type: "section", id: it.id, title: it.title || "" }
      : cleanTaskItem(it),
  );

// Clean the whole template object (every stage).
export const cleanTemplate = (template = {}) =>
  Object.keys(template).reduce((acc, stage) => {
    acc[stage] = cleanTemplateItems(template[stage] || []);
    return acc;
  }, {});

// Turn a stage's template items into deal-ready task objects:
// drop header rows, and stamp each task with the nearest section above it
// (null if none) plus the stage. Order is preserved.
export const expandStageItems = (items = [], stage = null, { coordinator = null, transactionCoordinator = null } = {}) => {
  if (!Array.isArray(items)) return [];
  let currentSection = null;
  const out = [];
  for (const it of items) {
    if (isSectionItem(it)) {
      currentSection = it.title || null;
      continue;
    }
    const base = { ...it, stage, section: currentSection };
    if (it.coordinatorRole) {
      base.coordinatorRole = true;
      base.assignee = coordinator || null;
    }
    if (it.transactionCoordinatorRole) {
      base.transactionCoordinatorRole = true;
      base.assignee = transactionCoordinator || null;
    }
    out.push(base);
  }
  return out;
};

// Group a stage's template items into ordered sections for the editor.
// Returns [{ section: {id,title} | null, tasks: [...] }].
export const groupTemplateItems = (items = []) => {
  const groups = [];
  let current = { section: null, tasks: [] };
  for (const it of items) {
    if (isSectionItem(it)) {
      if (current.section || current.tasks.length) groups.push(current);
      current = { section: { id: it.id, title: it.title || "" }, tasks: [] };
    } else {
      current.tasks.push(it);
    }
  }
  if (current.section || current.tasks.length) groups.push(current);
  return groups;
};

// done/total for a list of tasks (done = status "Completed").
export const sectionProgress = (tasks = []) => ({
  done: tasks.filter((t) => t.status === "Completed").length,
  total: tasks.length,
});

// Group a deal's flat tasks by stage (in stageOrder; unknown stages last)
// then by section (first-seen order). Returns
// [{ stage, sections: [{ title|null, tasks, done, total }] }].
export const groupDealTasks = (tasks = [], stageOrder = []) => {
  const byStage = new Map();
  for (const t of tasks) {
    const stage = t.stage || "Other";
    if (!byStage.has(stage)) byStage.set(stage, []);
    byStage.get(stage).push(t);
  }
  const known = stageOrder.filter((s) => byStage.has(s));
  const unknown = [...byStage.keys()].filter((s) => !stageOrder.includes(s));
  return [...known, ...unknown].map((stage) => {
    const stageTasks = byStage.get(stage);
    const order = [];
    const bySection = new Map();
    for (const t of stageTasks) {
      const key = t.section || "";
      if (!bySection.has(key)) {
        bySection.set(key, []);
        order.push(key);
      }
      bySection.get(key).push(t);
    }
    const sections = order.map((key) => {
      const secTasks = bySection.get(key);
      const { done, total } = sectionProgress(secTasks);
      return { title: key || null, tasks: secTasks, done, total };
    });
    return { stage, sections };
  });
};

// Active stages: STAGE_ORDER minus the finished ones.
export const ACTIVE_STAGES = STAGE_ORDER.filter(
  (s) => !["Closed", "Cancelled", "Expired"].includes(s),
);

// Build new tasks + tracker to backfill one deal from the template.
// Adds tasks for every stage up to & including deal.dealStage that the deal does
// NOT already have tasks for. Current stage -> "Open", earlier -> "Completed".
// Coordinator-role tasks resolve to `coordinator`. Existing deal.tasks are kept.
// NOTE: "already has this stage" is keyed off the deal's ACTUAL tasks, not
// taskStatusTracker — deals created while the template was empty have stages
// marked "applied" in the tracker yet hold no tasks, so trusting it would skip
// exactly the deals we need to backfill.
// Returns { tasks, taskStatusTracker } or null when there's nothing to add or the
// stage is unknown.
export const buildDealBackfill = (deal, template = {}, { coordinator = null, transactionCoordinator = null, stageOrder = STAGE_ORDER, nowIso = null } = {}) => {
  const current = deal?.dealStage;
  const idx = stageOrder.indexOf(current);
  if (idx < 0) return null;
  const allowed = stageOrder.slice(0, idx + 1);
  const existingStages = new Set((deal.tasks || []).map((t) => t.stage).filter(Boolean));
  const tracker = { ...(deal.taskStatusTracker || {}) };
  const added = [];
  for (const stage of allowed) {
    if (existingStages.has(stage)) continue;
    const stageTasks = expandStageItems(template[stage] || [], stage, { coordinator, transactionCoordinator }).map((t) => ({
      ...t,
      status: stage === current ? "Open" : "Completed",
      createdAt: nowIso,
    }));
    if (stageTasks.length === 0) continue;
    added.push(...stageTasks);
    tracker[stage] = true;
  }
  if (added.length === 0) return null;
  return { tasks: [...(deal.tasks || []), ...added], taskStatusTracker: tracker };
};

// Dry run for backfillActiveDeals: how much a backfill would actually change,
// without writing anything. Same skip rules as the real thing, so the numbers
// the confirm dialog shows are the numbers that will happen.
// Returns { active, needing, missing } — active deals considered, how many of
// them would gain tasks, and how many tasks would be added in total.
export const previewDealBackfill = (deals = [], template = {}) => {
  let active = 0;
  let needing = 0;
  let missing = 0;
  for (const deal of deals || []) {
    if (deal?.archived || !ACTIVE_STAGES.includes(deal?.dealStage)) continue;
    active++;
    const result = buildDealBackfill(deal, template, {});
    if (!result) continue;
    needing++;
    missing += result.tasks.length - (deal.tasks || []).length;
  }
  return { active, needing, missing };
};

// Find an agent's still-open coordinator tasks currently on a given coordinator.
// Returns [{ dealId, task }]. dealId uses deal.createdat (the deal doc id).
export const findAgentCoordinatorTasks = (deals = [], agentId, oldCoordId) => {
  const out = [];
  for (const deal of deals) {
    if (deal?.agentData !== agentId) continue;
    for (const task of deal.tasks || []) {
      if (
        task.coordinatorRole === true &&
        task.status !== "Completed" &&
        task.assignee?.id === oldCoordId
      ) {
        out.push({ dealId: String(deal.createdat), task });
      }
    }
  }
  return out;
};
